import React, { useEffect, useReducer } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, Navigate, Outlet } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import LocalLoader from "../../../../components/Loaders/Local";

import { isAuthenticated } from "../../../../services/auth.service";
import { showNotification } from "../../../core/slices/notification.slice";
import { isSSOEnabled, notifyError } from "../../../../utils/helpers";
import { setSessionData } from "../../slices/userSession.slice";

function RequireAuth(props) {
  const {
    showWhenUserLoggedOut = false,
    navConfigItem = false,
    allowedItems = [],
    conditionalNavConfigItem = false,
  } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const [, recheckAuth] = useReducer((count) => count + 1, 0);
  const { navConfig } = useSelector((state) => state.navConfig);
  const { isMaintenance } = useSelector((state) => state.maintenance);
  const { isLoggedOut, permissionList, userData } = useSelector(
    (state) => state.auth
  );

  let auth;

  const getAuthData = () => {
    try {
      auth = isAuthenticated();
    } catch (error) {
      notifyError(dispatch, showNotification, error);
    }
  };
  getAuthData();

  useEffect(() => {
    if (!isAuthenticated()) {
      const { pathname, search } = location;
      const postLoginRoute = pathname + search;
      dispatch(setSessionData({ postLoginRoute }));
      const savedPostLoginRoute = localStorage.getItem("postLoginRoute");
      if (!savedPostLoginRoute?.startsWith("/register?"))
        localStorage.setItem("postLoginRoute", postLoginRoute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // bfcache restores the page without re-mounting, so re-run the auth checks.
  useEffect(() => {
    if (!showWhenUserLoggedOut) return undefined;

    const handlePageShow = (event) => {
      if (event.persisted) recheckAuth();
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [showWhenUserLoggedOut]);

  const validateNavConfigItems = () => {
    const isPresent = navConfig?.items?.find(
      (item) => item.enabled && location.pathname.includes(item.link.slice(1))
    );
    return isPresent;
  };
  if (isMaintenance) return <Outlet />;
  if (auth) {
    if (userData?.addNewUser) {
      if (location.pathname === "/register") return <Outlet />;
      return <Navigate to="/register" state={{ from: location }} replace />;
    }
    if (
      (navConfigItem && validateNavConfigItems()) ||
      permissionList?.find((role) => allowedItems?.includes(role)) ||
      (conditionalNavConfigItem && validateNavConfigItems())
    )
      return <Outlet />;
    if (showWhenUserLoggedOut)
      return <Navigate to="/licenses" state={{ from: location }} replace />;
    if (navConfig && navConfig.items) {
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }
    return null;
  }
  if (showWhenUserLoggedOut && !auth) return <Outlet />;
  if (isSSOEnabled && !auth && isLoggedOut) {
    return (
      <Box sx={{ mt: "calc(50vh - 3.75rem)" }}>
        <LocalLoader progress />
      </Box>
    );
  }
  return <Navigate to="/login" state={{ from: location }} replace />;
}

RequireAuth.propTypes = {
  showWhenUserLoggedOut: PropTypes.bool,
  conditionalNavConfigItem: PropTypes.bool,
  navConfigItem: PropTypes.bool,
  allowedItems: PropTypes.arrayOf(PropTypes.string),
};
RequireAuth.defaultProps = {
  showWhenUserLoggedOut: false,
  conditionalNavConfigItem: false,
  navConfigItem: false,
  allowedItems: [],
};

export default RequireAuth;
